import { z } from "zod";

/**
 * Hizmet veren başvurusu ve profil yönetimi (K-010, K-020, K-025, K-034, K-039, K-062, K-066, DAC7).
 * Tutarlar küçük birimde metin (bigint güvenli); para birimi pazarın para birimi.
 */
const Minor = z.string().regex(/^\d{1,9}$/);
const Locale = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);

/** POST /v1/provider — başvuruyu başlat */
export const CreateProviderBody = z.object({
  type: z.enum(["individual", "company"]),
  displayName: z.string().trim().min(2).max(160),
  market: z.string().length(2).default("MT"),
});

/** PATCH /v1/provider — profil, yasal ve vergi bilgileri (yalnızca sahibi) */
export const UpdateProviderBody = z
  .object({
    displayName: z.string().trim().min(2).max(160),
    about: z.string().trim().max(2000),
    aboutLocale: Locale,
    /** kapak çizimi `<tür>:<sıra>`; null = otomatik */
    coverArt: z
      .string()
      .regex(/^[a-z_]{3,16}:[0-9]{1,2}$/)
      .nullable(),
    /** kendi yüklediği kapak fotoğrafı (provider-media kovası, `<hizmet veren>/<dosya>`); null = kaldır (K-165) */
    coverImagePath: z
      .string()
      .min(3)
      .max(255)
      .regex(/^[0-9a-f-]{36}\/[A-Za-z0-9._-]{1,200}$/)
      .nullable(),
    legalName: z.string().trim().max(200),
    registrationNumber: z.string().trim().max(64),
    vatNumber: z.string().trim().max(32),
    vatStatus: z.enum(["registered", "exempt_small", "unknown"]),
    taxId: z.string().trim().max(32),
    taxResidenceCountry: z.string().regex(/^[A-Z]{2}$/),
    dateOfBirth: z.string().date(),
    /** DAC7 bildirim onayı; yalnızca true gönderilir */
    dac7Consent: z.literal(true),
    /** bireysel: bağımsız çalışan onayı */
    soleProprietorAck: z.literal(true),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "en az bir alan" });

/** PATCH /v1/provider/members/:memberId — üye profili */
export const UpdateMemberBody = z
  .object({
    bio: z.string().trim().max(1000),
    bioLocale: Locale,
    spokenLanguages: z.array(Locale).max(10),
    bufferMinutes: z.number().int().min(0).max(180),
    canTakeJobs: z.boolean(),
    /** acil iş alır (K-050) */
    urgentOptIn: z.boolean(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "en az bir alan" });

/** PUT /v1/provider/services/:serviceCode — hizmet ve fiyat (K-020; hizmet veren belirler) */
export const UpsertProviderServiceBody = z.object({
  hourlyRateMinor: Minor.optional(),
  fixedPriceMinor: Minor.optional(),
  offersAtCustomer: z.boolean().default(true),
  offersAtProvider: z.boolean().default(false),
  travelFeeMinor: Minor.nullable().optional(),
  minMinutes: z
    .number()
    .int()
    .min(30)
    .max(24 * 60)
    .nullable()
    .optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  isActive: z.boolean().default(true),
});

/** PUT /v1/provider/members/:memberId/areas — K-025: yerleşim yerleri ve/veya bölge kısayolu */
export const ServiceAreasBody = z
  .object({
    localities: z.array(z.string().min(1).max(64)).max(100).default([]),
    regions: z.array(z.string().min(1).max(32)).max(5).default([]),
  })
  .refine((b) => b.localities.length + b.regions.length > 0, { message: "en az bir bölge" });

/** PUT /v1/provider/members/:memberId/availability — haftalık şablon (pazarın saatiyle) */
export const WeeklyAvailabilityBody = z.object({
  rules: z
    .array(
      z.object({
        weekday: z.number().int().min(0).max(6),
        start: z.string(),
        end: z.string(),
      }),
    )
    .max(28),
});

/** POST /v1/provider/members/:memberId/time-off — izin (veya ek müsaitlik) */
export const TimeOffBody = z.object({
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  isAvailable: z.boolean().default(false),
});

/** PUT /v1/provider/pause — tatil modu (K-161): bu zamana kadar yeni iş almaz; en çok 180 gün */
export const PauseBody = z.object({ until: z.string().datetime({ offset: true }) });

/** POST /v1/admin/providers/:id/reject */
export const RejectProviderBody = z.object({ reason: z.string().trim().min(3).max(500) });

export interface ProviderChecklistView {
  items: { key: string; done: boolean; required: boolean }[];
  ready: boolean;
}

/** Yüklenen dosyanın yolu (Supabase Storage kovası içinde, kovaya göre ilk klasör kural gereği) */
const StoragePath = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[0-9a-f-]{36}\/[A-Za-z0-9._-]{1,200}$/);

/** POST /v1/tasks/:id/media — K-052 fotoğraf/video kaydı (dosya önce task-media kovasına yüklenir) */
export const AddTaskMediaBody = z.object({
  stage: z.enum(["request", "before", "after", "dispute", "change_order", "pickup", "delivery"]),
  mediaType: z.enum(["image", "video"]),
  path: StoragePath,
});

/** POST /v1/provider/portfolio — K-054 (dosya önce provider-media kovasına) */
export const AddPortfolioItemBody = z.object({
  mediaType: z.enum(["image", "video", "audio"]),
  path: StoragePath,
  caption: z.string().trim().max(300).optional(),
  serviceCode: z.string().max(80).optional(),
});

/** POST /v1/provider/documents — K-043/K-039/K-050 belge (dosya önce verification kovasına) */
export const AddVerificationDocumentBody = z.object({
  type: z.enum([
    "police_conduct_certificate",
    "business_registration",
    "vat_registration",
    "insurance",
    "trade_license",
  ]),
  path: StoragePath,
  issueDate: z.string().date().optional(),
});

/** GET /v1/provider/documents — yüklenen belgelerin durumu (dosya yolu verilmez) */
export interface VerificationDocumentView {
  id: string;
  type:
    | "police_conduct_certificate"
    | "business_registration"
    | "vat_registration"
    | "insurance"
    | "trade_license";
  status: "not_started" | "pending" | "approved" | "rejected" | "expired";
  issueDate: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

/** POST /v1/provider/team/invitations — şirket çalışan daveti (K-010) */
export const InviteMemberBody = z
  .object({
    email: z.string().trim().toLowerCase().email().max(255),
    role: z.enum(["admin", "worker"]).default("worker"),
    canTakeJobs: z.boolean().default(true),
  })
  .strict();

/** PUT /v1/provider/members/:memberId/available-now — "şu an müsaitim" (K-050); 0 = kapat */
export const AvailableNowBody = z.object({ minutes: z.number().int().min(0).max(480) }).strict();

/** POST /v1/provider/tasks/:id/accept — acil işte varış süresi zorunlu (K-050) */
export const AcceptTaskBody = z.object({
  memberId: z.string().uuid().optional(),
  arrivalMinutes: z.number().int().min(1).max(240).optional(),
});

/** PUT /v1/provider/service-address — hizmeti kendi yerinde veren (salon, klinik) için iş yeri adresi (K-061) */
export const ServiceAddressBody = z.object({
  line1: z.string().trim().min(3).max(200),
  line2: z.string().trim().max(200).nullable().default(null),
  /** yerleşim yeri kodu (ör. sliema) */
  locality: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
  postcode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3} ?\d{2,4}$/)
    .nullable()
    .default(null),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});
export type ServiceAddressBody = z.input<typeof ServiceAddressBody>;

/** POST /v1/provider/menu, PUT /v1/provider/menu/:id — sabit fiyatlı menü kalemi (K-058, ör. "Jel manikür €25, 45 dk") */
export const MenuItemBody = z.object({
  serviceCode: z.string().min(1).max(80),
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(500).optional(),
  /** hizmet veren fiyatı (kuruş); müşteriye komisyon eklenerek gösterilir */
  priceMinor: z.string().regex(/^\d{1,9}$/),
  durationMinutes: z
    .number()
    .int()
    .min(5)
    .max(8 * 60),
  sortOrder: z.number().int().min(0).max(999).optional(),
});
export type MenuItemBody = z.infer<typeof MenuItemBody>;

/** GET /v1/provider/team/schedule — şirket sahibi/yöneticisi: üyelerin müsaitliği ve yaklaşan işleri (K-158) */
export interface TeamScheduleMember {
  memberId: string;
  /** haftalık çalışma saatleri tanımlı gün sayısı (0 = hiç saat girilmemiş, iş alamaz) */
  openDays: number;
  /** şu an "müsaitim" (acil) açık mı */
  availableNowUntil: string | null;
  /** önümüzdeki izin/engel aralıkları (en yakın 10) */
  timeOff: { startsAt: string; endsAt: string }[];
  /** yaklaşan atanmış işler (en yakın 25, 21 gün) */
  upcoming: {
    taskId: string;
    status: string;
    service: string | null;
    scheduledStart: string | null;
    scheduledMinutes: number | null;
  }[];
}
export interface TeamScheduleResponse {
  members: TeamScheduleMember[];
}
